import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data: { email: string; password: string }) => api.post('/auth/login', data),
  register: (data: { email: string; password: string; fullName: string }) => api.post('/auth/register', data),
  profile: () => api.get('/auth/profile'),
};

// Units
export const unitApi = {
  getAll: () => api.get('/units'),
  getById: (id: number) => api.get(`/units/${id}`),
  create: (data: any) => api.post('/units', data),
  update: (id: number, data: any) => api.put(`/units/${id}`, data),
};

// Owners
export const ownerApi = {
  getAll: () => api.get('/owners'),
  getById: (id: number) => api.get(`/owners/${id}`),
  create: (data: any) => api.post('/owners', data),
  update: (id: number, data: any) => api.put(`/owners/${id}`, data),
};

// Invoices
export const invoiceApi = {
  getAll: (params?: any) => api.get('/invoices', { params }),
  getById: (id: number) => api.get(`/invoices/${id}`),
  generate: (data: any) => api.post('/invoices/billing/generate-invoices', data),
};

// Payments
export const paymentApi = {
  getAll: (params?: any) => api.get('/payments', { params }),
  create: (data: any) => api.post('/payments', data),
};

// Fee Types
export const feeTypeApi = {
  getAll: () => api.get('/fee-types'),
  create: (data: any) => api.post('/fee-types', data),
  update: (id: number, data: any) => api.put(`/fee-types/${id}`, data),
  delete: (id: number) => api.delete(`/fee-types/${id}`),
};

// Fund Types
export const fundTypeApi = {
  getAll: () => api.get('/fund-types'),
  create: (data: any) => api.post('/fund-types', data),
  update: (id: number, data: any) => api.put(`/fund-types/${id}`, data),
  delete: (id: number) => api.delete(`/fund-types/${id}`),
};

// Accounting
export const accountingApi = {
  getChartOfAccounts: () => api.get('/chart-of-accounts'),
  createAccount: (data: any) => api.post('/chart-of-accounts', data),
  updateAccount: (id: number, data: any) => api.put(`/chart-of-accounts/${id}`, data),
  deleteAccount: (id: number) => api.delete(`/chart-of-accounts/${id}`),
  getJournalEntries: (params?: any) => api.get('/journal-entries', { params }),
  createJournalEntry: (data: any) => api.post('/journal-entries', data),
  getTrialBalance: () => api.get('/accounting/trial-balance'),
  getIncomeStatement: (params?: any) => api.get('/accounting/income-statement', { params }),
  getBalanceSheet: () => api.get('/accounting/balance-sheet'),
};

// Maintenance
export const maintenanceApi = {
  getRequests: (params?: any) => api.get('/maintenance-requests', { params }),
  createRequest: (data: any) => api.post('/maintenance-requests', data),
  updateStatus: (id: number, status: string) => api.put(`/maintenance-requests/${id}/status`, { status }),
};

// Amenities
export const amenityApi = {
  getAll: () => api.get('/amenities'),
  getAvailability: (id: number, date: string) => api.get(`/amenities/${id}/availability`, { params: { date } }),
  create: (data: any) => api.post('/amenities', data),
  update: (id: number, data: any) => api.put(`/amenities/${id}`, data),
  createBooking: (data: any) => api.post('/amenity-bookings', data),
  cancelBooking: (id: number) => api.delete(`/amenity-bookings/${id}`),
  getMyBookings: () => api.get('/amenity-bookings'),
};
